import { Icon } from '@launchpad-ui/icons';

import styles from './DiffBeforeAfter.module.css';

export type DiffBeforeAfterProps = {
  /**
   * Left side of the diff — rendered with strikethrough. Omit when the
   * before-state is indeterminate (e.g. applying a custom payload to an existing
   * key, where the prior counts aren't known): the strikethrough value and the
   * arrow are both dropped and only `after` renders.
   */
  before?: string;
  /** Right side of the diff — rendered as the post-change value. */
  after: string;
  /**
   * Optional accessible label for the whole diff cell. Consumers compose it with
   * the surrounding row context so screen readers announce "<row label> changing
   * from <before> to <after>" instead of reading the strikethrough text as-is.
   */
  ariaLabel?: string;
};

/**
 * The strikethrough "before" value and the decorative arrow that precede the
 * "after" value. Renders nothing when the before-state is indeterminate so only
 * the "after" value shows.
 */
function DiffBeforePrefix({ before }: { before: string | undefined }) {
  if (before === undefined) {
    return null;
  }
  return (
    <>
      <span className={styles.diffBefore}>{before}</span>
      {/* Decorative — the Icon defaults to aria-hidden since it has no label. */}
      <Icon name="arrow-right-thin" size="small" variant="subtle" className={styles.diffArrow} />
    </>
  );
}

/**
 * Shared visual atom for the "before → after" diff cell used across the
 * payload-change confirmation dialogs.
 *
 * Renders a strikethrough "before", a decorative arrow, and the "after" value.
 * When `before` is omitted (an indeterminate before-state), the strikethrough
 * value and the arrow are both dropped and only "after" renders. Numerics use
 * tabular figures so columns align across rows.
 */
export function DiffBeforeAfter({ before, after, ariaLabel }: DiffBeforeAfterProps) {
  return (
    <span className={styles.diffValues} aria-label={ariaLabel}>
      <DiffBeforePrefix before={before} />
      <span className={styles.diffAfter}>{after}</span>
    </span>
  );
}
